import { ISubscription } from '@leedpulse-monorepo/interfaces';
import { formatPhoneNumber } from '@leedpulse-monorepo/utils';
import UserDoc from '../../../../domain/models/UserDoc';
import { AiReceptionistListRow } from '../../../../domain/models/AiReceptionist';

export type ReceptionistAiStatus = 'En service' | 'Désactivé';

export interface ReceptionistAiRowDisplay {
  subscription_id: string;
  userName: string;
  userEmail: string;
  userAvatar: string;
  activitiesCount: number;
  motifsSummary: string;
  callsThisMonth: number;
  status: ReceptionistAiStatus;
}

export function getReceptionistAiStatus(
  row: Pick<AiReceptionistListRow, 'enabled'>,
): ReceptionistAiStatus {
  return row.enabled ? 'En service' : 'Désactivé';
}

export function getReceptionistAiStatusModifier(status: ReceptionistAiStatus): 'active' | 'disabled' {
  return status === 'En service' ? 'active' : 'disabled';
}

export function buildMotifsSummary(row: AiReceptionistListRow): string {
  const total = row.motifs.length;
  if (total === 0) return 'Aucun motif configuré';

  const custom = row.motifs.filter((motif) => motif.mode === 'custom' && motif.custom_instruction?.trim()).length;
  return `${total} motif${total > 1 ? 's' : ''} pris en charge, dont ${custom} avec une consigne personnalisée`;
}

interface BuildReceptionistAiRowsParams {
  rows: AiReceptionistListRow[];
  subscriptions: ISubscription[];
  users: UserDoc[];
}

export function buildReceptionistAiRows({
  rows,
  subscriptions,
  users,
}: BuildReceptionistAiRowsParams): ReceptionistAiRowDisplay[] {
  const subscriptionById = new Map(subscriptions.map((subscription) => [subscription.id, subscription]));

  return rows.map((row) => {
    const subscription = subscriptionById.get(row.subscription_id);
    const assignedUser = subscription ? users.find((user) => user.idSubscription?.[subscription.id]) : undefined;
    const userName = assignedUser ? `${assignedUser.firstname} ${assignedUser.lastname}`.trim() : '';
    // The indexed names are the numbers page's: they hold even when the company users are not loaded
    const indexedUserName = row.usernames?.join(', ') || '';
    const sender = row.sender ? formatPhoneNumber(row.sender) : '';

    return {
      subscription_id: row.subscription_id,
      userName: userName || indexedUserName || assignedUser?.email || sender || 'Non assigné',
      // the number tells apart the rows, it shows under the name like the email did
      userEmail: [assignedUser?.email, userName || indexedUserName ? sender : ''].filter(Boolean).join(' · '),
      userAvatar: assignedUser?.avatar || '',
      activitiesCount: row.activities.filter((activity) => activity.enabled).length,
      motifsSummary: buildMotifsSummary(row),
      callsThisMonth: row.stats.callsCount,
      status: getReceptionistAiStatus(row),
    };
  });
}
